import { App } from "vue";

export function labelPrintService() {
  return {
    install(app: App) {
      declare global {
        interface Window {
          labelPrintService: LabelPrintService;
        }
      }

      const globalProperties = app.config.globalProperties;
      window.labelPrintService = globalProperties.$labelPrintService = LabelPrintService();
    },
  };
}

export const LabelPrintService = () => {
  // Cached for the lifetime of one print sequence; cleared in print()'s finally
  // so the next print on this tab — and concurrent prints on other tabs — can
  // claim the USB interface afresh.
  let cachedDevice;

  const getSavedDevice = async () => {
    const saved = JSON.parse(localStorage.getItem("zebraPrinterInfo"));
    if (!saved) return null;

    const devices = await navigator.usb.getDevices();
    return devices.find((d) => d.vendorId === saved.vendorId && d.productId === saved.productId);
  };

  const requestAndSaveDevice = async () => {
    try {
      const device = await navigator.usb.requestDevice({
        filters: [{ vendorId: 0x0a5f }],
      });

      localStorage.setItem(
        "zebraPrinterInfo",
        JSON.stringify({
          vendorId: device.vendorId,
          productId: device.productId,
        }),
      );

      return device;
    } catch (error) {
      if (error.name === "NotFoundError") {
        console.warn("User cancelled the USB device selection.");
      } else {
        console.error("Unexpected error when selecting USB device:", error);
      }
      return null;
    }
  };

  const initDevice = async () => {
    const device = await getDevice();
    const usbInterface = await getUsbInterface();

    await device.claimInterface(usbInterface.interfaceNumber);
    await device.selectAlternateInterface(usbInterface.interfaceNumber, 0);

    return { device, interfaceNumber: usbInterface.interfaceNumber };
  };

  const getDevice = async () => {
    if (cachedDevice) {
      return cachedDevice;
    }

    cachedDevice = await getSavedDevice();
    if (!cachedDevice) {
      cachedDevice = await requestAndSaveDevice();
    }

    if (!cachedDevice) {
      return;
    }

    await cachedDevice.open();
    if (cachedDevice.configuration === null) {
      await cachedDevice.selectConfiguration(1);
    }

    return cachedDevice;
  };

  const releaseDevice = async (device, interfaceNumber) => {
    try {
      await device.releaseInterface(interfaceNumber);
    } catch (e) {
      console.warn("Failed to release USB interface:", e);
    }
    try {
      await device.close();
    } catch (e) {
      console.warn("Failed to close USB device:", e);
    }
    cachedDevice = undefined;
  };

  const getUsbInterface = async () => {
    const device = await getDevice();

    return device.configuration.interfaces.find((i) =>
      i.alternates.some((a) => a.endpoints.some((e) => e.direction === "out")),
    );
  };

  const getEndpoint = async () => {
    const usbInterface = await getUsbInterface();

    return usbInterface.alternates[0].endpoints.find((e) => e.direction === "out");
  };

  const getDeviceType = async () => {
    const device = await getDevice();
    if (!device) {
      return null;
    }

    if (
      device.productName.trim() == "LP2824" ||
      device.productName.trim() == "ZTC LP 2824 Plus" ||
      device.productName.trim() == "ZTC TLP 2824 Plus" ||
      device.productName.substring(device.productName.length - 8) === "TPL 2844"
    ) {
      return "EPL";
    } else if (
      device.productName.trim() == "LP2824-Z" ||
      device.productName.trim() == "ZTC ZD411-203dpi ZPL" ||
      device.productName.trim() == "ZTC ZD220-203dpi ZPL" ||
      device.productName.substring(device.productName.length - 4) === " ZPL" ||
      device.productName.substring(device.productName.length - 10) === "TPL 2844-Z" ||
      device.productName.match(new RegExp(/G[KV]420D/, "i")) !== null
    ) {
      return "ZPL";
    }

    return null;
  };

  const print = async (rawData) => {
    let device;
    let interfaceNumber;
    try {
      ({ device, interfaceNumber } = await initDevice());
      const deviceType = await getDeviceType();
      const endpoint = await getEndpoint();

      if ("EPL" === deviceType) {
        const encoder = new TextEncoder();
        const data = encoder.encode(rawData);
        await device.transferOut(endpoint.endpointNumber, data);

        return;
      } else if ("ZPL" === deviceType) {
        const encoder = new TextEncoder();
        // ENG-20690 switch the printer to UTF-8 input so multi-byte accented chars
        // in patient names render correctly. The trailing ^JUS save persists it.
        await device.transferOut(endpoint.endpointNumber, encoder.encode("^XA^CI28^XZ\n"));
        const data = encoder.encode(rawData);
        await device.transferOut(endpoint.endpointNumber, data);
        await device.transferOut(endpoint.endpointNumber, encoder.encode(`^XA\n^JUS\n^XZ`));

        return;
      }

      alert("Could not determine printer model: " + device.productName.trim());
    } catch (error) {
      console.error(error);
      alert("Error: " + error.message);
    } finally {
      if (device) {
        await releaseDevice(device, interfaceNumber);
      }
    }
  };

  return {
    getDeviceType,
    print,
  };
};
